import React, { useState } from "react";
import { Table, Button, Modal } from "react-bootstrap";
import { Eye } from "lucide-react";

const workNatureColors = {
  Support: { bg: "#FEF3E2", color: "#C2740C" },
  Testing: { bg: "#E0F2FE", color: "#0369A1" },
  Development: { bg: "#EDE9FE", color: "#6D28D9" },
};

// decimal hours (2, 1.5) -> "2:00", "1:30"
const toHHMM = (hours) => {
  const total = Math.round((Number(hours) || 0) * 60);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

// unique Work From values for the day: "Office", "Home" or "Office, Home"
const getWorkFrom = (row) => {
  const values = [
    ...new Set(
      (row.entries || [])
        .map((e) => e.workingFrom)
        .filter((v) => v && v !== "-")
    ),
  ];
  return values.length ? values.join(", ") : "-";
};

const fieldLabel = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "#7c3aed",
  marginBottom: 6,
};
const fieldValue = { fontSize: 14.5, color: "#111827", fontWeight: 500 };

const styles = {
  th: {
    background: "#2E5CB8",
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: 600,
    borderBottom: "1px solid #E5E7EB",
    padding: "12px 16px",
    position: "sticky",
    top: 0,
    zIndex: 2,
  },
  td: {
    fontSize: 13.5,
    color: "#111827",
    padding: "14px 16px",
    borderColor: "#F1F2F4",
    verticalAlign: "middle",
  },
  badge: {
    fontWeight: 600,
    fontSize: 12,
    padding: "4px 10px",
    borderRadius: 6,
  },
  pagerBtn: {
    border: "1px solid #E5E7EB",
    background: "#F9FAFB",
    color: "#9CA3AF",
    borderRadius: 6,
  },
  footnote: { fontSize: 13, color: "#6B7280" },
};

const ReportTable = ({
  paginatedRows,
  searchedCount,
  page,
  totalPages,
  pageSize,
  onPrevPage,
  onNextPage,
}) => {
  const [selectedRow, setSelectedRow] = useState(null);

  return (
    <>
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", overflowX: "auto" }}>
      <Table bordered hover style={{ borderCollapse: "collapse", marginBottom: 0 }}>
        <thead>
          <tr>
            <th style={styles.th}>Employee Name</th>
            <th style={styles.th}>Working Date</th>
            <th style={styles.th}>Reported Date</th>
            <th style={styles.th}>Work From</th>
            <th style={styles.th} className="text-end">Total Hours</th>
            <th style={styles.th} className="text-center">Action</th>
          </tr>
        </thead>
        <tbody>
          {paginatedRows.length > 0 ? (
            paginatedRows.map((row, i) => {

              return (
                <tr key={row.id ?? i}>
                  <td style={styles.td}>{row.employeeName}</td>
                  <td style={styles.td}>
                    {new Date(row.workingDate).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                    })}
                  </td>
                  <td style={styles.td}>
                    {row.reportedDate
                      ? new Date(row.reportedDate).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                        })
                      : "-"}
                  </td>
                   <td style={styles.td}>{getWorkFrom(row)}</td>
                  <td style={styles.td} className="text-end">{`${row.hours}h`}</td>
                  <td style={styles.td} className="text-center">
                    <Button
                      variant="light"
                      size="sm"
                      style={{ border: "1px solid #E5E7EB", borderRadius: 6 }}
                      onClick={() => setSelectedRow(row)}
                    >
                      <Eye size={16} color="#2F5FDC" />
                    </Button>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={6} className="text-center">
                No data found for the selected dates and search
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-2">
        <div style={styles.footnote}>
          Showing {searchedCount === 0 ? 0 : (page - 1) * pageSize + 1}–
          {Math.min(page * pageSize, searchedCount)} of {searchedCount}
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button
            variant="outline-secondary"
            size="sm"
            style={styles.pagerBtn}
            disabled={page === 1}
            onClick={onPrevPage}
          >
            ‹
          </Button>
          <span style={styles.footnote}>
            Page {page} of {totalPages}
          </span>
          <Button
            variant="outline-secondary"
            size="sm"
            style={styles.pagerBtn}
            disabled={page === totalPages}
            onClick={onNextPage}
          >
            ›
          </Button>
        </div>
      </div>

      <Modal
        show={!!selectedRow}
        onHide={() => setSelectedRow(null)}
        centered
        size="xl"
      >
        <Modal.Header closeButton>
          <div>
            <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>Work Details</Modal.Title>
            {selectedRow && (
              <div style={{ fontSize: 12.5, color: "#6B7280", marginTop: 2 }}>
                {selectedRow.employeeName} •{" "}
                {new Date(selectedRow.workingDate).toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}{" "}
                • {(selectedRow.entries || []).length}{" "}
                {(selectedRow.entries || []).length === 1 ? "entry" : "entries"} • Total {selectedRow.hours}h
              </div>
            )}
          </div>
        </Modal.Header>

        <Modal.Body style={{ maxHeight: "75vh", overflowY: "auto", background: "#F9FAFB" }}>
          {selectedRow &&
            (selectedRow.entries || []).map((entry, idx, arr) => {
              const nc = workNatureColors[entry.workNatureName] || {
                bg: "#f3f4f6",
                color: "#374151",
              };
              return (
                <div
                  key={entry.id ?? idx}
                  style={{
                    background: "#fff",
                    border: "1px solid #E5E7EB",
                    borderRadius: 10,
                    padding: "16px 20px",
                    marginBottom: idx === arr.length - 1 ? 0 : 14,
                  }}
                >
                  {/* Header: entry number + time */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      paddingBottom: 12,
                      marginBottom: 14,
                      borderBottom: "1px solid #E5E7EB",
                    }}
                  >
                    <strong style={{ fontSize: 14 }}>
                      Entry {idx + 1} of {arr.length}
                    </strong>
                    <span
                      style={{
                        background: "#ede9fe",
                        color: "#6d28d9",
                        fontWeight: 700,
                        fontSize: 13,
                        padding: "4px 12px",
                        borderRadius: 999,
                      }}
                    >
                      {toHHMM(entry.hours)} hrs
                    </span>
                  </div>

                  {/* Info grid */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                      gap: 18,
                      marginBottom: 16,
                    }}
                  >
                    <div>
                      <div style={fieldLabel}>Work From</div>
                      <span
                        style={{
                          display: "inline-block",
                          padding: "4px 12px",
                          borderRadius: 999,
                          fontSize: 12,
                          fontWeight: 600,
                          background: "#ede9fe",
                          color: "#6d28d9",
                        }}
                      >
                        {entry.workingFrom || "-"}
                      </span>
                    </div>
                    <div>
                      <div style={fieldLabel}>Client</div>
                      <div style={fieldValue}>{entry.clientName || "-"}</div>
                    </div>
                    <div>
                      <div style={fieldLabel}>Module</div>
                      <div style={fieldValue}>{entry.moduleName || "-"}</div>
                    </div>
                    <div>
                      <div style={fieldLabel}>Work Nature</div>
                      <span
                        style={{
                          display: "inline-block",
                          fontWeight: 600,
                          fontSize: 12.5,
                          padding: "4px 10px",
                          borderRadius: 6,
                          background: nc.bg,
                          color: nc.color,
                        }}
                      >
                        {entry.workNatureName || "-"}
                      </span>
                    </div>
                  </div>

                  {/* Task */}
                  <div>
                    <div style={fieldLabel}>Task</div>
                    <div
                      style={{
                        background: "#f9fafb",
                        border: "1px solid #E5E7EB",
                        borderRadius: 8,
                        padding: "12px 14px",
                        fontSize: 14,
                        lineHeight: 1.6,
                        color: "#111827",
                        whiteSpace: "pre-line",
                        wordBreak: "break-word",
                        overflowWrap: "anywhere",
                      }}
                    >
                      {entry.workDetails || "-"}
                    </div>
                  </div>
                </div>
              );
            })}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ReportTable;
